import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Box, Flex, LoadingOverlay, Radio, Text, noop } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { renterApi } from '@/shared/api/renter';
import useAppStore from '@/shared/store/app.store';
import { IRenter } from '@/shared/types/renter.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { IconBuilding } from '@tabler/icons-react';

type RenterSelectorProps = {
	value?: number | null;
	onChange: (renterId: number | null) => void;
};

export const RenterSelector = ({ value = null, onChange }: RenterSelectorProps) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const [searchValue, setSearchValue] = useState('');
	const [selectedId, setSelectedId] = useState<number | null>(value);

	const [opened, { close, open }] = useDisclosure(false);

	const { data: rentersData = [], isLoading } = useQuery({
		queryKey: ['renters'],
		queryFn: renterApi.getRenters,
		enabled: opened || value != null,
		staleTime: Infinity,
	});

	const filteredRenters = useMemo(() => {
		const query = searchValue.toLowerCase().trim();

		if (!query) {
			return rentersData;
		}

		return rentersData.filter((renter) => renter.renter_name.toLowerCase().includes(query));
	}, [rentersData, searchValue]);

	const selectedName = rentersData.find((renter) => renter.id === value)?.renter_name ?? '';

	useEffect(() => {
		setSelectedId(value);
	}, [value, opened]);

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.currentTarget.value);
	};

	const handleSelectRenter = (renter: IRenter) => {
		setSelectedId((current) => (current === renter.id ? null : renter.id));
	};

	const handleClose = () => {
		setSearchValue('');
		setSelectedId(value);
		close();
	};

	const handleSave = () => {
		onChange(selectedId);
		setSearchValue('');
		close();
	};

	const handleOpen = () => {
		setSearchValue('');
		setSelectedId(value);
		open();
	};

	const content = (
		<Box pos="relative" h="100%">
			<LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
			<Flex direction="column" mt={16}>
				{filteredRenters.map((renter) => (
					<LayoutRow
						key={renter.id}
						showDivider
						type="center"
						style={{ padding: 0, cursor: 'pointer' }}
						onClick={() => handleSelectRenter(renter)}
					>
						<Flex justify="space-between">
							<Text size="md" fz={16} fw={600} lts="normal">
								{renter.renter_name}
							</Text>
							<Radio
								checked={selectedId === renter.id}
								onChange={noop}
								styles={{ root: { pointerEvents: 'none' } }}
							/>
						</Flex>
					</LayoutRow>
				))}
				{!isLoading && !filteredRenters.length && (
					<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
						<Text fz={18} fw={700}>
							Пока ничего нет
						</Text>
					</Flex>
				)}
			</Flex>
		</Box>
	);

	const buttons = [
		{
			children: 'Выбрать',
			onClick: handleSave,
		},
	];

	return (
		<>
			<LabelInput
				label="Арендатор"
				placeholder="Выбрать из списка"
				onClick={handleOpen}
				value={selectedName}
				rightSection={<IconBuilding opacity={0.5} />}
				readOnly
			/>

			{isMobile ? (
				<BottomSheet
					opened={opened}
					onClose={handleClose}
					trapFocus={false}
					title="Выбрать арендатора"
					buttons={buttons}
					search={{
						onChange: handleSearch,
						value: searchValue,
					}}
					withBackButton={false}
					withCloseButton
					isFullScreen
				>
					{content}
				</BottomSheet>
			) : (
				<ModalSheet
					opened={opened}
					onClose={handleClose}
					title="Выбрать арендатора"
					buttons={buttons}
					search={{
						onChange: handleSearch,
						value: searchValue,
					}}
					size="lg"
					centered
				>
					<Box style={{ overflowY: 'auto' }} h="60dvh" pr="md">
						{content}
					</Box>
				</ModalSheet>
			)}
		</>
	);
};
